import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api";
import type { MrpIncomingFeedResponse, MrpInventoryRow, MrpInventorySublocationRow, MrpSkuHoverHintFields } from "@/lib/mrp";

import {
  buildKittingCartSlots,
  incomingRowsEligibleForKitting,
  kittingCartSlotById,
  type KittingCartSlot,
} from "./kittingCartModel";
import type { KittingBatchPeekMap } from "./kittingBatchPeek";
import {
  buildKittingPickLines,
  buildPickRouteAlphabetical,
  type KittingHubSublocationBalance,
  type KittingPickLine,
} from "./kittingPickModel";

const KITTING_QUERY_STALE_MS = 5 * 60 * 1000;

type SkuHoverHintPayload = Record<string, MrpSkuHoverHintFields>;

function displayNameFromHoverHint(
  cpn: string,
  hoverHints: SkuHoverHintPayload | undefined,
): string {
  const hint = hoverHints?.[cpn];
  if (hint == null) return cpn;
  const parts = [hint.description, hint.variant].filter((part) => part != null && String(part).trim() !== "");
  return parts.length > 0 ? parts.join(" — ") : cpn;
}

export function useKittingPageData(): {
  cartSlots: KittingCartSlot[];
  cartSlotById: Map<string, KittingCartSlot>;
  pickLines: KittingPickLine[];
  pickRoute: ReturnType<typeof buildPickRouteAlphabetical>;
  batchPeeksById: KittingBatchPeekMap;
  batchPeekLoading: boolean;
  hasKittingJobs: boolean;
  floorPickLoading: boolean;
  loading: boolean;
  error: ApiError | null;
  hoverHints: SkuHoverHintPayload | undefined;
  hoverHintsLoading: boolean;
  incomingLoading: boolean;
  incomingError: ApiError | null;
} {
  const inventoryQuery = useQuery<{ rows: MrpInventoryRow[] }, ApiError>({
    queryKey: ["mrp", "inventory", "kitting", "sgs"],
    queryFn: () =>
      apiFetch<{ rows: MrpInventoryRow[] }>("/api/mrp/inventory", {
        query: { location: "sgs", pick_route: "1" },
      }),
    staleTime: KITTING_QUERY_STALE_MS,
    refetchOnWindowFocus: false,
  });

  const incomingQuery = useQuery<MrpIncomingFeedResponse, ApiError>({
    queryKey: ["mrp", "incoming", "kitting", "sgs"],
    queryFn: () =>
      apiFetch<MrpIncomingFeedResponse>("/api/mrp/incoming", {
        query: {
          location: "sgs",
          include_sku_hints: "0",
          work_order_incoming_mode: "components",
          incoming_kinds: "work_order,transfer_order",
        },
      }),
    staleTime: KITTING_QUERY_STALE_MS,
    refetchOnWindowFocus: false,
  });

  const incomingForKitting = useMemo(
    () => incomingRowsEligibleForKitting(incomingQuery.data?.rows ?? []),
    [incomingQuery.data?.rows],
  );

  const batchIdsForLookup = useMemo(() => {
    const ids = new Set<number>();
    for (const row of incomingForKitting) {
      if (row.kind !== "work_order") continue;
      for (const batchId of row.batch_ids ?? []) {
        if (Number.isFinite(batchId)) ids.add(batchId);
      }
    }
    return Array.from(ids).sort((left, right) => left - right);
  }, [incomingForKitting]);

  const batchPeekQuery = useQuery<{ peeks: KittingBatchPeekMap }, ApiError>({
    queryKey: ["manufacturing", "batch-peeks", "kitting", batchIdsForLookup.join(",")],
    queryFn: () =>
      apiFetch<{ peeks: KittingBatchPeekMap }>("/api/manufacturing/batch-peeks", {
        query: { ids: batchIdsForLookup.join(",") },
      }),
    enabled: batchIdsForLookup.length > 0,
    staleTime: KITTING_QUERY_STALE_MS,
    refetchOnWindowFocus: false,
  });

  const batchPeeksById = batchPeekQuery.data?.peeks ?? {};

  const batchMfrShipByById = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const batchId of batchIdsForLookup) {
      map.set(batchId, batchPeeksById[batchId]?.mfr_ship_by_date ?? null);
    }
    return map;
  }, [batchIdsForLookup, batchPeeksById]);

  const cartSlots = useMemo(
    () => buildKittingCartSlots(incomingForKitting, { batchMfrShipByById }),
    [incomingForKitting, batchMfrShipByById],
  );

  const cartSlotById = useMemo(() => kittingCartSlotById(cartSlots), [cartSlots]);

  const hasKittingJobs = cartSlots.length > 0;

  const sgsInventoryRows = useMemo(() => {
    const rows = inventoryQuery.data?.rows ?? [];
    return rows.filter((row) => row.location_code === "sgs");
  }, [inventoryQuery.data?.rows]);

  const cpnsForHoverHints = useMemo(() => {
    if (!hasKittingJobs) return [];
    const cpns = new Set<string>();
    for (const row of incomingForKitting) {
      if (row.location_code !== "sgs") continue;
      const cpn = String(row.cpn ?? "").trim();
      if (cpn.length > 0) cpns.add(cpn);
    }
    return Array.from(cpns).slice(0, 200);
  }, [hasKittingJobs, incomingForKitting]);

  const sublocationQuery = useQuery<{ rows: MrpInventorySublocationRow[] }, ApiError>({
    queryKey: ["mrp", "inventory-sublocations", "kitting", "sgs", cpnsForHoverHints.join("\n")],
    queryFn: () =>
      apiFetch<{ rows: MrpInventorySublocationRow[] }>("/api/mrp/inventory-sublocations", {
        query: { location: "sgs", cpns: cpnsForHoverHints.join(",") },
      }),
    enabled: cpnsForHoverHints.length > 0,
    staleTime: KITTING_QUERY_STALE_MS,
    refetchOnWindowFocus: false,
  });

  const hubSublocationsByCpn = useMemo(() => {
    const map = new Map<string, KittingHubSublocationBalance[]>();
    for (const row of sublocationQuery.data?.rows ?? []) {
      if (row.location_code !== "sgs") continue;
      const cpn = String(row.cpn ?? "").trim();
      if (cpn.length === 0) continue;
      const list = map.get(cpn) ?? [];
      list.push({
        sublocation: row.sublocation ?? "",
        quantity: Math.round(Number(row.quantity) || 0),
      });
      map.set(cpn, list);
    }
    return map;
  }, [sublocationQuery.data?.rows]);

  const hoverHintsQuery = useQuery<{ sku_hover_hints: SkuHoverHintPayload }, ApiError>({
    queryKey: ["mrp", "sku-hover-hints", "kitting", cpnsForHoverHints.join("\n")],
    queryFn: () =>
      apiFetch<{ sku_hover_hints: SkuHoverHintPayload }>("/api/mrp/sku-hover-hints", {
        query: { cpns: cpnsForHoverHints.join(",") },
      }),
    enabled: cpnsForHoverHints.length > 0,
    staleTime: KITTING_QUERY_STALE_MS,
    refetchOnWindowFocus: false,
  });

  const pickLines = useMemo(() => {
    if (!hasKittingJobs) return [];
    const tips = hoverHintsQuery.data?.sku_hover_hints;
    return buildKittingPickLines({
      incomingRows: incomingForKitting,
      inventoryRows: sgsInventoryRows,
      hubSublocationsByCpn,
      cartSlotById,
      displayNameForCpn: (cpn) => displayNameFromHoverHint(cpn, tips),
    });
  }, [
    hasKittingJobs,
    incomingForKitting,
    sgsInventoryRows,
    hubSublocationsByCpn,
    cartSlotById,
    hoverHintsQuery.data?.sku_hover_hints,
  ]);

  const pickRoute = useMemo(() => buildPickRouteAlphabetical(pickLines), [pickLines]);

  const floorPickLoading =
    incomingQuery.isLoading ||
    (hasKittingJobs && batchIdsForLookup.length > 0 && batchPeekQuery.isLoading) ||
    (hasKittingJobs && inventoryQuery.isLoading) ||
    (hasKittingJobs && cpnsForHoverHints.length > 0 && sublocationQuery.isLoading);

  const loading = floorPickLoading;
  const error =
    inventoryQuery.error ??
    incomingQuery.error ??
    batchPeekQuery.error ??
    sublocationQuery.error ??
    null;

  return {
    cartSlots,
    cartSlotById,
    pickLines,
    pickRoute,
    batchPeeksById,
    batchPeekLoading: batchPeekQuery.isLoading,
    hasKittingJobs,
    floorPickLoading,
    loading,
    error,
    hoverHints: hoverHintsQuery.data?.sku_hover_hints,
    hoverHintsLoading: hoverHintsQuery.isLoading,
    incomingLoading: incomingQuery.isLoading,
    incomingError: incomingQuery.error ?? null,
  };
}
